'use client';

import * as Headless from '@headlessui/react';
import clsx from 'clsx';
import * as React from 'react';
import { Fragment, useMemo,useState } from 'react';
import { HiMagnifyingGlass, HiXMark } from 'react-icons/hi2';

import { Badge } from './badge';
import { Button } from './button';
import { Input, InputGroup } from './input';

export function MultiListbox<T extends { id?: any, code?: any, name?: string, label?: string }>({
    className,
    placeholder,
    autoFocus,
    searchPlaceholder = 'Search...',
    'aria-label': ariaLabel,
    children: options,
    value = [],
    onChange,
    getDisplayValue,
    getSearchValue,
    ...props
}: {
    className?: string
    placeholder?: React.ReactNode
    searchPlaceholder?: string
    autoFocus?: boolean
    'aria-label'?: string
    children?: React.ReactNode
    value?: T[]
    onChange?: (value: T[]) => void
    getDisplayValue?: (item: T) => string
    getSearchValue?: (item: T) => string
} & Omit<Headless.ListboxProps<typeof Fragment, T[]>, 'as' | 'multiple' | 'value' | 'onChange'>) {
    const [searchValue, setSearchValue] = useState('');

    // Extract options from children
    const optionsList = useMemo(() => {
        if (!options) return [];
        return React.Children.toArray(options).map((child: any) => child.props.value).filter(Boolean);
    }, [options]);

    // Filter options based on search
    const filteredOptions = useMemo(() => {
        if (!searchValue.trim()) return optionsList;
        return optionsList.filter((option: T) => {
            const searchIn = getSearchValue ? getSearchValue(option) : (option.label || option.name || '');
            return searchIn.toLowerCase().includes(searchValue.toLowerCase());
        });
    }, [optionsList, searchValue, getSearchValue]);

    // Generate display text for button
    const displayText = useMemo(() => {
        if (value.length === 0) return placeholder;
        if (value.length === 1) {
            return getDisplayValue ? getDisplayValue(value[0]) : (value[0].label || value[0].name);
        }
        if (value.length === optionsList.length) return 'All selected';
        return `${value.length} selected`;
    }, [value, placeholder, optionsList.length, getDisplayValue]);

    const handleSelectAll = () => {
        onChange?.(filteredOptions);
    };

    const handleClearAll = () => {
        onChange?.([]);
    };

    const isSelected = (option: T) => {
        const key = option.id ?? option.code;
        return value.some(v => (v.id ?? v.code) === key);
    };

    const handleToggle = (option: T) => {
        const key = option.id ?? option.code;
        const isCurrentlySelected = value.some(v => (v.id ?? v.code) === key);

        if (isCurrentlySelected) {
            onChange?.(value.filter(v => (v.id ?? v.code) !== key));
        } else {
            onChange?.([...value, option]);
        }
    };

    return (
        <Headless.Listbox value={value} onChange={onChange} multiple>
            <Headless.ListboxButton
                autoFocus={autoFocus}
                data-slot="control"
                aria-label={ariaLabel}
                className={clsx([
                    className,
                    // Basic layout
                    'group relative block w-full',
                    // Background color + shadow applied to inset pseudo element, so shadow blends with border in light mode
                    'before:absolute before:inset-px before:rounded-[calc(theme(borderRadius.lg)-1px)] before:bg-white before:shadow',
                    // Background color is moved to control and shadow is removed in dark mode so hide `before` pseudo
                    'dark:before:hidden',
                    // Hide default focus styles
                    'focus:outline-none',            // Focus ring
            'after:pointer-events-none after:absolute after:inset-0 after:rounded-lg after:ring-inset after:ring-transparent after:data-[focus]:ring-2 after:data-[focus]:ring-secondary-600',
                    // Disabled state
                    'data-[disabled]:opacity-50 before:data-[disabled]:bg-zinc-950/5 before:data-[disabled]:shadow-none',
                ])}
            >
                <span
                    className={clsx([
                        // Basic layout
                        'relative block w-full appearance-none rounded-lg py-[calc(theme(spacing[2.5])-1px)] sm:py-[calc(theme(spacing[1.5])-1px)]',
                        // Set minimum height for when no value is selected
                        'min-h-11 sm:min-h-9',
                        // Horizontal padding
                        'pl-[calc(theme(spacing[3.5])-1px)] pr-[calc(theme(spacing.7)-1px)] sm:pl-[calc(theme(spacing.3)-1px)]',
                        // Typography
                        'text-left text-base/6 text-zinc-950 placeholder:text-zinc-500 dark:text-white sm:text-sm/6 forced-colors:text-[CanvasText]',
                        // Border
                        'border border-zinc-950/10 group-data-[active]:border-zinc-950/20 group-data-[hover]:border-zinc-950/20 dark:border-white/10 dark:group-data-[active]:border-white/20 dark:group-data-[hover]:border-white/20',
                        // Background color
                        'bg-transparent dark:bg-white/5',
                        // Invalid state
                        'group-data-[invalid]:border-red-500 group-data-[invalid]:group-data-[hover]:border-red-500 group-data-[invalid]:dark:border-red-600 group-data-[invalid]:data-[hover]:dark:border-red-600',
                        // Disabled state
                        'group-data-[disabled]:border-zinc-950/20 group-data-[disabled]:opacity-100 group-data-[disabled]:dark:border-white/15 group-data-[disabled]:dark:bg-white/[2.5%] dark:data-[hover]:group-data-[disabled]:border-white/15',
                        // Placeholder styling
                        value.length === 0 ? 'text-zinc-500' : 'text-zinc-950 dark:text-white'
                    ])}
                >
                    {displayText}
                </span>
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <svg
                        className="size-5 stroke-zinc-500 group-data-[disabled]:stroke-zinc-600 dark:stroke-zinc-400 sm:size-4 forced-colors:stroke-[CanvasText]"
                        viewBox="0 0 16 16"
                        aria-hidden="true"
                        fill="none"
                    >
                        <path d="M5.75 10.75L8 13L10.25 10.75" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M10.25 5.25L8 3L5.75 5.25" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </span>
            </Headless.ListboxButton>

            <Headless.ListboxOptions
                transition
                anchor="selection start"
                className={clsx(
                    // Anchor positioning
                    '[--anchor-offset:-1.625rem] [--anchor-padding:theme(spacing.4)] sm:[--anchor-offset:-1.375rem]',
                    // Base styles
                    'isolate w-max min-w-[calc(var(--button-width)+1.75rem)] select-none scroll-py-1 rounded-xl p-1',
                    // Invisible border that is only visible in `forced-colors` mode for accessibility purposes
                    'outline outline-1 outline-transparent focus:outline-none',
                    // Handle scrolling when menu won't fit in viewport
                    'overflow-y-scroll overscroll-contain',
                    // Popover background
                    'bg-white/75 backdrop-blur-xl dark:bg-zinc-800/75',
                    // Shadows
                    'shadow-lg ring-1 ring-zinc-950/10 dark:ring-inset dark:ring-white/10',
                    // Transitions
                    'transition-opacity duration-100 ease-in data-[transition]:pointer-events-none data-[closed]:data-[leave]:opacity-0',
                    // Z-index
                    'z-30',
                    // Set max width and height
                    'max-h-80 max-w-xs'
                )}
            >
                {/* Search Input */}
                <div className="border-b border-zinc-950/10 p-2 dark:border-white/10">
                    <InputGroup>
                        <HiMagnifyingGlass className="size-4"/>
                        <Input
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            placeholder={searchPlaceholder}
                            className="text-sm"
                        />
                    </InputGroup>
                </div>

                {/* Select All / Clear All / Clear Selection Buttons */}
                <div className="border-b border-zinc-950/10 p-2 dark:border-white/10 space-y-2">
                    <Button
                        outline
                        onClick={value.length === 0 || value.length < filteredOptions.length ? handleSelectAll : handleClearAll}
                        disabled={filteredOptions.length === 0}
                        className="w-full px-3 py-2 text-sm font-bold"
                    >
                        {value.length === 0 || value.length < filteredOptions.length ? 'Select All' : 'Clear All'}
                    </Button>
                    {value.length > 0 && (
                        <Button
                            outline
                            onClick={handleClearAll}
                            className="w-full px-3 py-2 text-sm font-bold"
                        >
                            Clear Selection
                        </Button>
                    )}
                </div>

                {/* Options */}
                <div className="max-h-64 overflow-y-auto">
                    {filteredOptions.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-zinc-500 dark:text-zinc-400">
                            No items found
                        </div>
                    ) : (
                        filteredOptions.map((option: T) => {
                            const key = option.id ?? option.code;
                            const isOptionSelected = isSelected(option);
                            const displayValue = getDisplayValue ? getDisplayValue(option) : (option.label || option.name || '');

                            return (
                                <Headless.ListboxOption
                                    key={key}
                                    value={option}
                                    className={({ active }) => clsx(
                                        'relative cursor-pointer select-none py-2 pl-8 pr-4 text-sm',
                                        active ? 'bg-primary-950/5 text-primary-950 dark:bg-white/10 dark:text-white' : 'text-zinc-900 dark:text-zinc-100'
                                    )}
                                    onClick={() => handleToggle(option)}
                                >
                                    <span className={clsx('block truncate', isOptionSelected ? 'font-medium' : 'font-normal')}>
                                        {displayValue}
                                    </span>
                                    <span className="absolute inset-y-0 left-0 flex items-center pl-2">
                                        <div className={clsx(
                                            'flex size-4 items-center justify-center rounded border-2',
                                            isOptionSelected 
                                                ? 'border-secondary-600 bg-secondary-600 dark:border-secondary-400 dark:bg-secondary-400' 
                                                : 'border-zinc-300 bg-white dark:border-zinc-600 dark:bg-zinc-800'
                                        )}>
                                            {isOptionSelected && (
                                                <svg className="size-3 text-white dark:text-zinc-900" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                                                </svg>
                                            )}
                                        </div>
                                    </span>
                                </Headless.ListboxOption>
                            );
                        })
                    )}
                </div>
            </Headless.ListboxOptions>
        </Headless.Listbox>
    );
}

export function MultiListboxOption<T>({
    children,
    className,
    ...props
}: { className?: string; children?: React.ReactNode } & Omit<
    Headless.ListboxOptionProps<'div', T>,
    'as' | 'className'
>) {
    return (
        <Headless.ListboxOption as="div" className={className} {...props}>
            {children}
        </Headless.ListboxOption>
    );
}